import { Text } from '@dixa/lib-shared-habla';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { CustomFieldAttribute } from 'apis/customFields';
import { HookFormSelectionListSingle } from 'components/hookForm';
import { ElevionLeafFieldID } from 'utils/elevion';
import { convertTreeToFlatItems } from 'utils/tree';

import { customAttributeTypeAttributes } from '../../../../../../../../../utils/customAttributeTypeAttributes';
import { AttributeFormFieldValues } from '../../../../../../../customAttributeModalForm.types';
import {
  isLeafItem,
  renderItem,
  renderItemOrList,
  SelectionListItemData,
  SelectionListLeafItemData,
} from '../renderItemOrList';

export type LeafFieldFieldProps = {
  parentName: string;
  customFields: CustomFieldAttribute[];
};

function getAttributeLabelByValue(
  value: string,
  items: readonly SelectionListItemData[],
): string | null {
  for (const item of items) {
    if (item.value === value) {
      return item.title;
    }
    if ('items' in item) {
      const title = getAttributeLabelByValue(value, item.items);
      if (title) {
        return title;
      }
    }
  }

  return null;
}

export const LeafFieldField = ({
  parentName,
  customFields,
}: LeafFieldFieldProps): JSX.Element => {
  // TODO: [TEAMS-13693] remove omit after updating to RHF v8
  const { control } =
    useFormContext<Omit<AttributeFormFieldValues, 'options'>>();
  const fieldName = `${parentName}.field` as 'visibility_condition.field';
  const identifier = useWatch({
    control,
    name: 'identifier',
  });
  const [filter, setFilter] = useState<string>('');

  const mapCustomFieldToSelect = useCallback(
    (field: CustomFieldAttribute): SelectionListItemData => ({
      value: `custom__${field.identifier}`,
      title: field.label,
      icon: customAttributeTypeAttributes[field.type].icon,
      // When editing a field, the field being edited should not be an option for the visibility condition
      // Because it would be a circular reference, we still display the field (disabled) as it makes for better UX
      disabled: field.identifier === identifier,
      tooltip:
        field.identifier === identifier ? 'This field is being edited' : '',
    }),
    [identifier],
  );

  const selectItems: SelectionListItemData[] = useMemo(
    () => [
      {
        title: 'Custom attributes',
        items: [...customFields.map(mapCustomFieldToSelect)],
        icon: 'input',
      },
      { value: ElevionLeafFieldID.Queue, title: 'Queue', icon: 'flow' },
    ],
    [customFields, mapCustomFieldToSelect],
  );

  const filteredItems = useMemo<readonly SelectionListLeafItemData[]>(() => {
    if (!filter) {
      return [];
    }

    const flatItems = convertTreeToFlatItems(selectItems, {
      childrenKey: 'items' as keyof SelectionListLeafItemData,
      transformItem: (item) => item,
    });

    return flatItems.filter(
      (item) =>
        isLeafItem(item) &&
        item.title.toLocaleLowerCase().includes(filter.toLocaleLowerCase()),
    ) as SelectionListLeafItemData[];
  }, [filter, selectItems]);

  const inputSelectionListProps = useMemo<
    ComponentProps<typeof HookFormSelectionListSingle<string>>['componentProps']
  >(
    () => ({
      getItemTitleByValue: (value: string | null | undefined) => (
        <Text.Paragraph>
          {value ? getAttributeLabelByValue(value, selectItems) : null}
        </Text.Paragraph>
      ),
      placeholder: 'Select a field',
      filter: {
        value: filter,
        onChange: (event) => setFilter(event.target.value),
      },
      onToggleDropdown: (isOpen) => isOpen && setFilter(''),
      selectionListProps: { style: { maxHeight: 320, overflow: 'auto' } },
      dropdownProps: { style: { minWidth: 200, width: 'auto' } },
    }),
    [filter, selectItems],
  );

  return (
    <HookFormSelectionListSingle
      name={fieldName}
      control={control}
      noLabel
      noAlert
      componentProps={inputSelectionListProps}
    >
      {filter
        ? filteredItems.map((item) => renderItem(item))
        : selectItems.map((item) => renderItemOrList(item))}
    </HookFormSelectionListSingle>
  );
};
